// ============================================================
// ReviewManager.jsx
// Manages both MANUAL and AUTOMATIC review prompts using the
// Shopify App Bridge Reviews API.
//
// IMPORTANT — Shopify's API response codes:
//   "success"          → modal was DISPLAYED (not submitted!)
//   "already-reviewed" → merchant HAS actually left a review
//   "cooldown-period"  → rate-limited, modal not shown
//   "cancelled"        → merchant closed the modal
//   … and others
//
// The ONLY reliable signal that a review was actually submitted
// is "already-reviewed" on a SUBSEQUENT call.  We therefore:
//
//   • "success"          → record the prompt (for rate tracking)
//                          but do NOT set review_submitted.
//   • "already-reviewed" → set review_submitted = true in DB
//                          + localStorage.  Auto-popup stops.
//   • anything else      → do nothing.
//
// RULES
// ─────
// 1. Manual (Share Feedback button):
//    → ALWAYS call shopify.reviews.request().
//    → No pre-checks of any kind.
//
// 2. Automatic (dashboard load):
//    → If review_submitted == true → skip.
//    → Otherwise show after 2 s delay.
//    → Keeps showing every load until Shopify says
//      "already-reviewed".
// ============================================================

import { useEffect, useCallback, useRef } from "react";
import { useAppBridge } from "@shopify/app-bridge-react";
import axios from "axios";

// localStorage key: "true" = merchant has confirmed-submitted
// a review (Shopify returned "already-reviewed").
const LS_REVIEW_SUBMITTED_KEY = "bloghero_review_submitted";

// ────────────────────────────────────────────────────────────
// localStorage helpers
// ────────────────────────────────────────────────────────────
function isReviewSubmittedLocally() {
  try {
    return localStorage.getItem(LS_REVIEW_SUBMITTED_KEY) === "true";
  } catch {
    return false;
  }
}

function markReviewSubmittedLocally() {
  try {
    localStorage.setItem(LS_REVIEW_SUBMITTED_KEY, "true");
  } catch {
    /* ignore */
  }
}

// ────────────────────────────────────────────────────────────
// Server helpers
// ────────────────────────────────────────────────────────────

/** Record that the modal was SHOWN (rate-limit tracking only). */
async function recordPromptOnServer(shopOrigin, type) {
  try {
    await axios.post("/api/record-review-prompt/", {
      shop: shopOrigin,
      type,
    });
  } catch (err) {
    console.error("[ReviewManager] Failed to record prompt:", err);
  }
}

/**
 * Mark review_submitted = true in the DB.
 * Called ONLY when Shopify confirms "already-reviewed".
 */
async function markSubmittedOnServer(shopOrigin) {
  try {
    await axios.post("/api/mark-review-submitted/", {
      shop: shopOrigin,
    });
  } catch (err) {
    console.error("[ReviewManager] Failed to mark submitted:", err);
  }
}

/** Check the DB flag. */
async function fetchReviewSubmittedFromServer(shopOrigin) {
  try {
    const res = await axios.get(
      `/api/get-review-status/?shop=${shopOrigin}`
    );
    return !!res.data.review_submitted;
  } catch {
    return false;
  }
}

// ────────────────────────────────────────────────────────────
// Core: call shopify.reviews.request() and handle the response
// ────────────────────────────────────────────────────────────
async function requestReviewModal(shopify, shopOrigin, type) {
  try {
    console.log(
      `[ReviewManager] Calling shopify.reviews.request() (${type})`
    );
    const result = await shopify.reviews.request();
    console.log("[ReviewManager] Shopify response:", result);

    // ── "success" → modal was DISPLAYED ─────────────────────
    // NOTE: On development/test stores Shopify ALWAYS returns
    // "success" and never "already-reviewed", even after the
    // merchant submits a review.  This is by design — dev
    // stores bypass all rate limits and submission tracking.
    // On a real production store "already-reviewed" will be
    // returned on the next call after the merchant submits.
    // We only record the prompt for rate-limit tracking here.
    // We do NOT set review_submitted.
    if (result.success && result.code === "success") {
      console.log(`[ReviewManager] Modal displayed (${type})`);
      recordPromptOnServer(shopOrigin, type);
      return { success: true, code: "success" };
    }

    // ── "already-reviewed" → merchant ACTUALLY reviewed ─────
    // This is the ONLY confirmation from Shopify that a
    // review was truly submitted.  NOW we mark submitted.
    // This code is NEVER returned on dev/test stores.
    if (!result.success && result.code === "already-reviewed") {
      console.log("[ReviewManager] Shopify confirms: already reviewed");
      markReviewSubmittedLocally();
      markSubmittedOnServer(shopOrigin);
      return { success: false, code: "already-reviewed" };
    }

    // ── Everything else → do nothing ────────────────────────
    console.log(
      `[ReviewManager] Modal not displayed: ${result.code} – ${result.message}`
    );
    return { success: false, code: result.code, message: result.message };

  } catch (error) {
    console.error("[ReviewManager] shopify.reviews.request() threw:", error);

    // Fallback: open App Store review page
    window.open(
      "https://apps.shopify.com/bloghero#modal-show=WriteReviewModal",
      "_blank"
    );
    return { success: false, code: "fallback", fallback: true };
  }
}

// ============================================================
// COMPONENT: ReviewManager
// ============================================================
export default function ReviewManager({ shopOrigin, onTriggerAvailable }) {
  const shopify = useAppBridge();
  const hasAutoTriggered = useRef(false);

  // ==========================================================
  // TRIGGER REVIEW MODAL
  // ==========================================================
  const triggerReviewModal = useCallback(
    async (type = "manual") => {
      try {
        console.log(`[ReviewManager] Trigger requested (${type})`);

        // ── Manual → always call, no checks ──────────────────
        if (type === "manual") {
          return await requestReviewModal(shopify, shopOrigin, type);
        }

        // ── Automatic → skip if already submitted ────────────
        if (isReviewSubmittedLocally()) {
          console.log("[ReviewManager] Auto skipped (localStorage)");
          return { success: false, code: "review_already_submitted" };
        }

        const submittedOnServer =
          await fetchReviewSubmittedFromServer(shopOrigin);
        if (submittedOnServer) {
          markReviewSubmittedLocally();
          console.log("[ReviewManager] Auto skipped (server)");
          return { success: false, code: "review_already_submitted" };
        }

        return await requestReviewModal(shopify, shopOrigin, type);

      } catch (error) {
        console.error("[ReviewManager] triggerReviewModal error:", error);
        return { success: false, code: "error", message: error.message };
      }
    },
    [shopOrigin, shopify]
  );

  // ==========================================================
  // AUTOMATIC PROMPT (on dashboard load)
  // ==========================================================
  useEffect(() => {
    if (!shopOrigin || hasAutoTriggered.current) return;

    const checkAndTrigger = async () => {
      try {
        hasAutoTriggered.current = true;

        // Fast local gate
        if (isReviewSubmittedLocally()) {
          console.log("[ReviewManager] Auto skipped (localStorage)");
          return;
        }

        // Server eligibility (install-age + submitted check)
        try {
          const response = await axios.get(
            `/api/get-review-eligibility/?shop=${shopOrigin}&type=automatic`
          );
          const { eligible, reason } = response.data;
          if (!eligible) {
            console.log("[ReviewManager] Auto not eligible:", reason);
            if (reason === "review_already_submitted") {
              markReviewSubmittedLocally();
            }
            return;
          }
        } catch (err) {
          console.error("[ReviewManager] Eligibility check failed:", err);
        }

        // Wait 2 s then show
        setTimeout(async () => {
          const result = await triggerReviewModal("automatic");
          console.log("[ReviewManager] Auto result:", result);
        }, 2000);

      } catch (error) {
        console.error("[ReviewManager] Auto error:", error);
      }
    };

    checkAndTrigger();
  }, [shopOrigin, triggerReviewModal]);

  // ==========================================================
  // PROVIDE TRIGGER TO PARENT
  // ==========================================================
  useEffect(() => {
    if (onTriggerAvailable) {
      onTriggerAvailable(triggerReviewModal);
    }
  }, [triggerReviewModal, onTriggerAvailable]);

  return null;
}

// ============================================================
// HOOK: useReviewTrigger (manual only, no pre-checks)
// ============================================================
export function useReviewTrigger(shopOrigin) {
  const shopify = useAppBridge();

  const triggerReview = useCallback(async () => {
    try {
      console.log("[useReviewTrigger] Manual trigger");
      return await requestReviewModal(shopify, shopOrigin, "manual");
    } catch (error) {
      console.error("[useReviewTrigger] Error:", error);
      return { success: false, code: "error", message: error.message };
    }
  }, [shopOrigin, shopify]);

  return triggerReview;
}
